// EventCard.js
import React, { useState } from 'react';
import { IoLocationOutline } from "react-icons/io5";
import IconButton from '../../shared/IconButton';
import ActionButton from '../../shared/ActionButton';
import { AnalyticIcon, CalendarIcon, LinkIcon } from '../../../assets/icons/icons';
import { IoSettingsOutline } from "react-icons/io5";
import Badge from '../../shared/Badge';
import { Link, useNavigate } from 'react-router';
import { handleAnalyticsClick } from '../../shared/AnalyticNavigatefunc';
import { MdOutlineDeleteOutline } from "react-icons/md";
import Modal from "../../shared/ErrorModal"
import { VscFeedback } from "react-icons/vsc";
import { BiExport } from "react-icons/bi";
import { getEngagementRate } from '../../../services/getEngagementRate';
import { FaRegClock } from "react-icons/fa6";

/**
 * @typedef {object} Organizer
 * @property {string} _id - The unique ID of the organizer.
 * @property {string} userName - The name of the organizer.
 */

/**
 * @typedef {object} Event
 * @property {string} _id - The unique ID of the event.
 * @property {string} eventName - The name of the event.
 * @property {string} status - The current status of the event ('Active', 'Completed', 'Upcoming', etc.).
 * @property {number} [expectedAttendees] - The estimated number of attendees.
 * @property {number} [clickCount] - The total number of taps/clicks recorded for the event.
 * @property {number} [postEventClickCount] - Clicks recorded after the event date (if applicable).
 * @property {string} eventStartDate - The start date string (ISO format).
 * @property {string} eventEndDate - The end date string (ISO format).
 * @property {string} location - The physical location of the event.
 * @property {string} baseUrl - The URL encoded on the event bracelet/NFC tag.
 * @property {string} destinationUrl - The URL the tap redirects to.
 * @property {Organizer[]} [organizers] - An array of associated organizers.
 */

/**
 * A card component displaying key information and actions for a single event.
 *
 * @param {object} props - The component's props.
 * @param {Event} props.event - The event data object to display.
 * @param {function(): void} props.onEdit - Callback function for when the Edit button is clicked.
 * @param {(eventId: string) => void} props.deleteEvent - Callback function to delete the event, invoked after modal confirmation.
 * @returns {React.ReactElement} The rendered Event Card component.
 */


const EventCard = ({ event, onEdit, deleteEvent }) => {
    console.log("---eventCart ", event)
    const navigate = useNavigate();
    const [clickEvent, setClickedEvent] = useState(null);
    const engagementRate = getEngagementRate(event?.expectedAttendees, event.clickCount);


    const formatDate = (isoString) => {
        return new Date(isoString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const formatTime = (isoString) => {
        return new Date(isoString).toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            timeZone: "UTC",
        });
    };


    const handleDeleteClick = (event) => {
        setClickedEvent(event)
        Modal({
            title: "Do You Want To Delete?",
            message: `Are you sure you want to delete ${event.eventName}?`,
            buttontxt: { accept: "Yes, Delete", decline: "No, Cancel" },
            onAccept: () => deleteEvent(event._id),
            onDecline: () => setClickedEvent(null),
            showAcceptDecline: true
        });
    };
    const handleFeedbackClick = (event) => {
        console.log("event", event)
        navigate(`/admin/feedback?eventId=${event._id}`)
    }
    return (
        <div className="bg-[var(--color-surface-background)] rounded-2xl border border-[var(--border-color)] p-4 sm:p-6 md:p-6 lg:p-8 w-full  mx-auto mb-2">

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center  mb-4">
                <div className="flex items-center space-x-3 mb-2 sm:mb-0 justify-between">
                    <h3 className="text-lg sm:text-xl font-semibold text-[var(--primary-text-color)]truncate">{event.eventName}</h3>
                    <Badge
                        label={event.status}
                        bgColor={
                            event.status === 'Active'
                                ? 'bg-[#fafafa]'
                                : event.status === 'Completed'
                                    ? 'bg-[#262626]'
                                    : 'bg-transparent'
                        }
                        textColor={event.status === 'Completed' ? 'text-white' : 'text-black'}
                        borderColor={event.status === 'Upcoming' ? 'border-gray-700' : ''}
                    />
                </div>
                <div className="flex items-center space-x-2">
                    <IconButton
                        icon={AnalyticIcon}
                        label="Analytics"
                        onClick={() =>
                            handleAnalyticsClick(event?._id, event?.clickCount, engagementRate, event?.postEventClickCount, event?.expectedAttendees, navigate)
                        }
                        hoverColor="hover:bg-[var(--primary-text-color)]"
                        bgColor="bg-[var(--color-surface-background)]"
                        border={true}
                    />
                    <IconButton
                        icon={IoSettingsOutline}
                        label="Edit"
                        onClick={onEdit}
                        hoverColor="hover:bg-[var(--primary-text-color)]"
                        bgColor="bg-[var(--color-surface-background)]"
                        border={true}
                    />
                    <button
                        className={`text-white px-4 py-2 rounded-lg flex items-center space-x-2 cursor-pointer bg-[var(--color-surface-background)] hover:bg-[var(--primary-text-color)] border border-[var(--border-color)]`}
                        onClick={() => { handleDeleteClick(event) }}
                    >
                        <span className='text-xl text-[var(--color-primary)]' ><MdOutlineDeleteOutline /></span>
                    </button>
                    <button
                        className={`text-white px-4 py-2 rounded-lg flex items-center space-x-2 cursor-pointer bg-[var(--color-surface-background)] hover:bg-[var(--primary-text-color)] border border-[var(--border-color)]`}
                        onClick={() => { handleFeedbackClick(event) }}
                    >
                        <span className='text-xl text-[var(--color-primary)]' ><VscFeedback /></span>
                    </button>
                </div>
            </div>

            {/* Event Info */}
            <div className="flex flex-col sm:flex-row sm:space-x-5 text-sm text-[var(--secondary-text-color)] mb-4 space-y-1 sm:space-y-0 ">
                <div className="flex space-x-5 sm:flex-row flex-col mb-3 sm:mb-0 sm:space-y-0  space-y-2 item-center">
                    <div className="flex items-center space-x-3 ">
                        <CalendarIcon className="w-4 h-4" />
                        {/* DATE RANGE */}
                        <span>
                            {formatDate(event.eventStartDate)} - {formatDate(event.eventEndDate)}
                        </span>
                    </div>
                    <div className="flex items-center space-x-3  ">
                        <FaRegClock className="w-4 h-4 me-2" />
                        {/* TIME RANGE IN PROPER AM/PM FORMAT */}

                        {formatTime(event.eventStartDate)} - {formatTime(event.eventEndDate)}

                    </div>


                </div>

                <div className="flex items-center space-x-1">
                    <IoLocationOutline />
                    <span>{event.location}</span>
                </div>
            </div>


            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                    <div className="text-[var(--secondary-text-color)]text-sm mb-1">Attendance</div>
                    <div className="text-[var(--primary-text-color)] text-xl font-semibold">{event?.expectedAttendees ?? '0'}</div>
                </div>
                <div>
                    <div className="text-[var(--secondary-text-color)] text-sm mb-1">Total Taps</div>
                    <div className="text-[var(--primary-text-color)] text-xl font-semibold">{event?.clickCount ?? '0'}</div>
                </div>
                <div>
                    <div className="text-[var(--secondary-text-color)] text-sm mb-1">Engagement Rate</div>
                    <div className="text-[var(--primary-text-color)] text-xl font-semibold">{engagementRate}%</div>
                </div>
                <div>
                    <div className="text-[var(--secondary-text-color)] text-sm mb-1">Organizer</div>
                    {event?.organizers?.length > 0 ? (
                        event.organizers.map((org) => (
                            <div key={org._id} className="text-[var(--secondary-text-color)] text-sm truncate">{org.userName}</div>
                        ))
                    ) : (
                        <div className="text-[var(--primary-text-color)] text-sm">No Organizer Assigned</div>
                    )}
                </div>
            </div>

            {/* URLs */}
            <div className="space-y-2">

                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 text-sm ">
                    <span className="text-[var(--secondary-text-color)] flex gap-1 items-center mb-1 sm:mb-0"><LinkIcon /> Bracelet URL:</span>
                    <Link to={event?.baseUrl} target="_blank" rel="noopener noreferrer">
                        <ActionButton label={event?.baseUrl.slice(0, 50) + "..." ?? 'N/A'} />
                    </Link>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 text-sm mt-2">
                    <span className="text-[var(--secondary-text-color)] flex gap-1 items-center mb-1 sm:mb-0"><LinkIcon /> Destination URL:</span>
                    <Link target="_blank" rel="noopener noreferrer" to={event?.destinationUrl}>
                        <ActionButton label={event?.destinationUrl?.trim() ? event.destinationUrl.slice(0, 50) + "..." : 'N/A'} />
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default EventCard;   